import { EnlargedSearchResultContent, SquareReturnIcon } from '@atlassian/search-dialog';
import React, { type ReactElement, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { messages } from '../../messages';
import {
	type SearchResult,
	type SearchResultSection,
	useProductContext,
} from '../../../product-router';
import { useQuery } from '../../../query-context';
import { useAdvancedSearchAnalyticsCallbacks } from '../../../search-result';
import { AdvancedSearchIcon } from '../../icons';
import { AdditionalMetaTextWrapper, AdditionalMetaWrapper } from './sections.styled';
import { PersistRecentSearch } from '../../../recent-searches/persist-recent-search';

export const SHOW_ALL_RESULTS_SECTION_ID = 'show-all-results';

const AdditionalMeta: React.FC<{}> = () => {
	return (
		<AdditionalMetaWrapper>
			<AdditionalMetaTextWrapper>
				<FormattedMessage {...messages.quick_find_show_organization_results} />
			</AdditionalMetaTextWrapper>
			<SquareReturnIcon />
		</AdditionalMetaWrapper>
	);
};

export const useShowAllResultsSection = (): SearchResultSection => {
	const { query } = useQuery();
	const { getActiveProduct } = useProductContext();
	const advancedSearchUrl = getActiveProduct()?.generateAdvancedSearchUrl?.(query);

	const showAllResultsSection: SearchResultSection = useMemo(() => {
		const showAllResultsItem: SearchResult = {
			id: 'show-all-results',
			url: advancedSearchUrl || '',
			title: query,
			meta: '',
			additionalMeta: <AdditionalMeta />,
			icon: <AdvancedSearchIcon />,
		};

		return {
			id: SHOW_ALL_RESULTS_SECTION_ID,
			title: '',
			searchResults: [showAllResultsItem],
			resultRenderer: ({ searchResult, isKeyboardHighlighted, isCollapsed }) => (
				<PersistRecentSearch>
					<EnlargedSearchResultContent
						{...searchResult}
						icon={searchResult.icon as ReactElement}
						isKeyboardHighlighted={isKeyboardHighlighted}
						isCollapsed={isCollapsed}
						returnIcon="never"
					/>
				</PersistRecentSearch>
			),
			useAnalyticsOverride: () =>
				useAdvancedSearchAnalyticsCallbacks({
					actionSubjectId: 'showAllResults',
				}),
		};
	}, [advancedSearchUrl, query]);

	return showAllResultsSection;
};
